import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AddressesPanel,
  AuditPanel,
  ConsentsPanel,
  IdentityPanel,
  NextOfKinPanel,
  OverviewPanel,
} from '@/features/customers/customer-panels'
import { CustomerAmendAction } from '@/features/customers/amend-action'
import { CustomerExposureBanner } from '@/features/customers/exposure-banner'
import { KycActions } from '@/features/customers/kyc-actions'
import { MediaPanel } from '@/features/customers/media-panel'
import { useCustomer } from '@/features/customers/queries'
import { CustomerStatusAction } from '@/features/customers/status-change-dialog'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { useIsAtLeast } from '@/lib/use-viewport'

import type { CustomerDetailTab } from './search'

const PANEL_ID = 'customer-detail-panel'

/**
 * The customer file.
 *
 * The exposure banner sits directly under the header, above everything else on the screen and
 * before any tab has to be chosen. It is the control that stops a customer in arrears at one
 * branch quietly borrowing at another, and it only works if it is read before a decision is made
 * rather than found afterwards.
 *
 * That ordering is what moves the KYC and status controls below the banner on a narrow screen.
 * At desktop width they sit top-right of the header and the banner is still the first thing under
 * the name; stacked, the same markup would put a row of decision buttons above the finding those
 * decisions turn on.
 */
export function CustomerDetailPage() {
  const { customerId } = useParams({ from: '/authenticated/customers/$customerId' })
  const search = useSearch({ from: '/authenticated/customers/$customerId' })
  const navigate = useNavigate()
  const actionsInHeader = useIsAtLeast('md')

  // An absent parameter is the overview rather than a blank screen, which is also what a link to
  // the customer with no tab at all should land on.
  const tab = search.tab ?? 'overview'

  const query = useCustomer(customerId)

  const selectTab = (next: CustomerDetailTab) => {
    void navigate({
      to: '/customers/$customerId',
      params: { customerId },
      search: { tab: next },
      // Moving between tabs is not a place someone means to come back to with the Back button.
      replace: true,
    })
  }

  return (
    <>
      <Link
        to="/customers"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-md:min-h-11 max-md:py-2"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All customers
      </Link>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-9 w-full max-w-xl" />
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This customer could not be opened"
        />
      ) : (
        <>
          <PageHeader
            title={query.data.fullName}
            reference={query.data.customerNumber}
            badges={
              <>
                <StatusBadge status={query.data.status} />
                <StatusBadge status={query.data.kycStatus} />
                <StatusBadge status={query.data.riskRating} />
              </>
            }
            actions={
              actionsInHeader ? (
                <>
                  <CustomerAmendAction customer={query.data} />
                  <CustomerStatusAction customer={query.data} />
                  <KycActions customer={query.data} />
                </>
              ) : null
            }
          />

          <CustomerExposureBanner exposure={query.data.exposure} />

          {!actionsInHeader ? (
            <div className="flex flex-wrap items-center gap-2">
              <CustomerAmendAction customer={query.data} />
              <CustomerStatusAction customer={query.data} />
              <KycActions customer={query.data} />
            </div>
          ) : null}

          <div className="space-y-4">
            <TabStrip<CustomerDetailTab>
              tabs={
                [
                  { id: 'overview', label: 'Overview' },
                  { id: 'addresses', label: 'Addresses', badge: query.data.addresses.length },
                  { id: 'identity', label: 'Identity', badge: query.data.identityDocuments.length },
                  { id: 'nextOfKin', label: 'Next of kin', badge: query.data.nextOfKin.length },
                  { id: 'consents', label: 'Consents', badge: query.data.consents.length },
                  { id: 'media', label: 'Media', badge: query.data.media.length },
                  { id: 'audit', label: 'Audit trail' },
                ] satisfies TabDefinition<CustomerDetailTab>[]
              }
              active={tab}
              onChange={selectTab}
              panelId={PANEL_ID}
              label="Customer record sections"
            />

            <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
              {tab === 'overview' ? <OverviewPanel customer={query.data} /> : null}
              {tab === 'addresses' ? <AddressesPanel addresses={query.data.addresses} /> : null}
              {tab === 'identity' ? <IdentityPanel documents={query.data.identityDocuments} /> : null}
              {tab === 'nextOfKin' ? <NextOfKinPanel nextOfKin={query.data.nextOfKin} /> : null}
              {tab === 'consents' ? <ConsentsPanel consents={query.data.consents} /> : null}
              {tab === 'media' ? <MediaPanel customer={query.data} /> : null}
              {tab === 'audit' ? <AuditPanel customer={query.data} /> : null}
            </div>
          </div>
        </>
      )}
    </>
  )
}
